import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building, Edit, Plus, Settings2, UserRound } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { API_URL, getAuthHeaders, formatPhoneNumber } from "../../lib/utils";
import { useApiMessages } from "../../hooks/useApiMessages";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { PageHeader, ScopeFilters } from "../../components/visitors/VisitorShared";
import { visitorApi } from "../../components/visitors/visitorUtils";

const HOST_TYPES = ["employee", "resident", "department", "contact", "other"];

export default function VisitorSetup() {
  const { t } = useTranslation();
  const scope = useVisitorScope();
  const needsCompany = scope.canSelectCompany && scope.companyId === "all";

  return (
    <div className="space-y-6">
      <PageHeader title={t("visitors.setup.title")} subtitle={t("visitors.setup.subtitle")} />
      <ScopeFilters scope={scope} showFacility={false} />
      {needsCompany && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">{t("visitors.setup.selectCompany")}</CardContent>
        </Card>
      )}
      <Tabs defaultValue="facilities">
        <TabsList>
          <TabsTrigger value="facilities">
            <Building className="h-4 w-4 mr-2" />
            {t("visitors.setup.facilities")}
          </TabsTrigger>
          <TabsTrigger value="hosts">
            <UserRound className="h-4 w-4 mr-2" />
            {t("visitors.setup.hosts")}
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings2 className="h-4 w-4 mr-2" />
            {t("visitors.setup.settings")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="facilities" className="mt-4">
          <FacilitiesTab scope={scope} disabled={needsCompany} />
        </TabsContent>
        <TabsContent value="hosts" className="mt-4">
          <HostsTab scope={scope} disabled={needsCompany} />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          {!needsCompany && <SettingsTab scope={scope} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const FacilitiesTab = ({ scope, disabled }) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const [facilities, setFacilities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      setFacilities(await visitorApi.get("/facilities", { company_id: scope.companyId, include_inactive: true }));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (f) => {
    setEditing(f || "new");
    setForm(
      f
        ? { ...f, access_points_text: (f.access_points || []).join(", ") }
        : { name: "", address: "", facility_type: "", access_points_text: "", require_id_verification: false, notes: "", is_active: true }
    );
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error(t("visitors.setup.nameRequired"));
    const payload = {
      name: form.name,
      address: form.address || null,
      facility_type: form.facility_type || null,
      access_points: (form.access_points_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      require_id_verification: !!form.require_id_verification,
      notes: form.notes || null,
    };
    try {
      if (editing === "new") await visitorApi.post("/facilities", { ...payload, company_id: scope.effectiveCompanyId });
      else await visitorApi.put(`/facilities/${editing.id}`, { ...payload, is_active: !!form.is_active });
      toast.success(t("visitors.setup.saved"));
      setEditing(null);
      load();
      scope.reloadFacilities();
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t("visitors.setup.facilities")}</CardTitle>
        <Button onClick={() => open(null)} disabled={disabled}>
          <Plus className="h-4 w-4 mr-2" />
          {t("visitors.setup.newFacility")}
        </Button>
      </CardHeader>
      <CardContent>
        {facilities.length === 0 ? (
          <p className="text-center py-8 text-slate-500">{t("visitors.setup.noFacilities")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("visitors.setup.facilityName")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("visitors.setup.accessPoints")}</TableHead>
                <TableHead className="hidden sm:table-cell">{t("visitors.setup.requireId")}</TableHead>
                <TableHead>{t("visitors.common.status")}</TableHead>
                <TableHead className="text-right">{t("visitors.common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {facilities.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <p className="font-medium">{f.name}</p>
                    <p className="text-xs text-slate-500">{[f.facility_type, f.address].filter(Boolean).join(" · ")}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{(f.access_points || []).join(", ") || "—"}</TableCell>
                  <TableCell className="hidden sm:table-cell">{f.require_id_verification ? t("visitors.common.yes") : t("visitors.common.no")}</TableCell>
                  <TableCell>
                    <Badge variant={f.is_active ? "secondary" : "outline"}>
                      {f.is_active ? t("visitors.setup.active") : t("visitors.setup.inactive")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => open(f)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("visitors.setup.newFacility") : t("visitors.setup.editFacility")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1 block">{t("visitors.setup.facilityName")} *</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block">{t("visitors.setup.facilityType")}</Label>
                <Input
                  placeholder={t("visitors.setup.facilityTypePlaceholder")}
                  value={form.facility_type || ""}
                  onChange={(e) => setForm({ ...form, facility_type: e.target.value })}
                />
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.setup.address")}</Label>
                <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="mb-1 block">{t("visitors.setup.accessPoints")}</Label>
              <Input value={form.access_points_text || ""} onChange={(e) => setForm({ ...form, access_points_text: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">{t("visitors.setup.accessPointsHelp")}</p>
            </div>
            <div>
              <Label className="mb-1 block">{t("visitors.common.notes")}</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={!!form.require_id_verification} onCheckedChange={(c) => setForm({ ...form, require_id_verification: !!c })} />
              <span className="text-sm">{t("visitors.setup.requireIdHelp")}</span>
            </label>
            {editing !== "new" && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={!!form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: !!c })} />
                <span className="text-sm">{t("visitors.setup.active")}</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t("visitors.common.cancel")}
            </Button>
            <Button onClick={save}>{t("visitors.common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const HostsTab = ({ scope, disabled }) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const [hosts, setHosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      setHosts(await visitorApi.get("/hosts", { company_id: scope.companyId, include_inactive: true }));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    axios
      .get(`${API_URL}/users`, { headers: getAuthHeaders() })
      .then((r) => setUsers(r.data.filter((u) => u.is_active)))
      .catch(() => setUsers([]));
  }, []);

  const facilityName = (id) => scope.facilities.find((f) => f.id === id)?.name;

  const open = (h) => {
    setEditing(h || "new");
    setForm(h ? { ...h, phone: formatPhoneNumber(h.phone || "") } : { name: "", host_type: "employee", unit: "", phone: "", email: "", facility_id: null, user_id: null, is_active: true });
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error(t("visitors.setup.nameRequired"));
    const payload = {
      name: form.name,
      host_type: form.host_type,
      unit: form.unit || null,
      phone: form.phone || null,
      email: form.email || null,
      facility_id: form.facility_id || null,
      user_id: form.user_id || null,
    };
    try {
      if (editing === "new") await visitorApi.post("/hosts", { ...payload, company_id: scope.effectiveCompanyId });
      else await visitorApi.put(`/hosts/${editing.id}`, { ...payload, is_active: !!form.is_active });
      toast.success(t("visitors.setup.saved"));
      setEditing(null);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  const companyId = editing && editing !== "new" ? editing.company_id : scope.effectiveCompanyId;
  const companyUsers = users.filter((u) => u.company_id === companyId);
  const companyFacilities = scope.facilities.filter((f) => f.company_id === companyId);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t("visitors.setup.hosts")}</CardTitle>
        <Button onClick={() => open(null)} disabled={disabled}>
          <Plus className="h-4 w-4 mr-2" />
          {t("visitors.setup.newHost")}
        </Button>
      </CardHeader>
      <CardContent>
        {hosts.length === 0 ? (
          <p className="text-center py-8 text-slate-500">{t("visitors.setup.noHosts")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("visitors.common.name")}</TableHead>
                <TableHead className="hidden sm:table-cell">{t("visitors.setup.hostType")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("visitors.common.facility")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("visitors.setup.linkedUser")}</TableHead>
                <TableHead className="text-right">{t("visitors.common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hosts.map((h) => (
                <TableRow key={h.id} className={h.is_active ? "" : "opacity-50"}>
                  <TableCell>
                    <p className="font-medium">{h.name}</p>
                    <p className="text-xs text-slate-500">{[h.unit, h.phone].filter(Boolean).join(" · ")}</p>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{t(`visitors.hostTypes.${h.host_type}`)}</TableCell>
                  <TableCell className="hidden md:table-cell">{facilityName(h.facility_id) || t("visitors.common.allFacilities")}</TableCell>
                  <TableCell className="hidden md:table-cell">{users.find((u) => u.id === h.user_id)?.name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => open(h)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("visitors.setup.newHost") : t("visitors.setup.editHost")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1 block">{t("visitors.common.name")} *</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block">{t("visitors.setup.hostType")}</Label>
                <Select value={form.host_type} onValueChange={(v) => setForm({ ...form, host_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOST_TYPES.map((k) => (
                      <SelectItem key={k} value={k}>
                        {t(`visitors.hostTypes.${k}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.setup.unit")}</Label>
                <Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.common.phone")}</Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="(809) 555-1234"
                  value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: formatPhoneNumber(e.target.value) })}
                />
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.common.email")}</Label>
                <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="mb-1 block">{t("visitors.common.facility")}</Label>
              <Select value={form.facility_id || "__all"} onValueChange={(v) => setForm({ ...form, facility_id: v === "__all" ? null : v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">{t("visitors.common.allFacilities")}</SelectItem>
                  {companyFacilities.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block">{t("visitors.setup.linkedUser")}</Label>
              <Select value={form.user_id || "__none"} onValueChange={(v) => setForm({ ...form, user_id: v === "__none" ? null : v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">{t("visitors.common.none")}</SelectItem>
                  {companyUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} · {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">{t("visitors.setup.linkedUserHelp")}</p>
            </div>
            {editing !== "new" && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={!!form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: !!c })} />
                <span className="text-sm">{t("visitors.setup.active")}</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t("visitors.common.cancel")}
            </Button>
            <Button onClick={save}>{t("visitors.common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const SettingsTab = ({ scope }) => {
  const { t } = useTranslation();
  const { canManageCompanies } = useAuth();
  const { getErrorMessage } = useApiMessages();
  const [settings, setSettings] = useState(null);
  const companyId = scope.effectiveCompanyId;

  useEffect(() => {
    visitorApi
      .get("/settings", { company_id: companyId })
      .then(setSettings)
      .catch((error) => toast.error(getErrorMessage(error, "visitors.errors.loadError")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const save = async () => {
    try {
      setSettings(
        await visitorApi.put(
          "/settings",
          {
            retention_days: Number(settings.retention_days) || 0,
            notify_host_on_arrival: !!settings.notify_host_on_arrival,
            max_visit_hours: Number(settings.max_visit_hours) || 0,
          },
          { company_id: companyId }
        )
      );
      toast.success(t("visitors.setup.saved"));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  const applyRetention = async () => {
    if (!window.confirm(t("visitors.setup.retentionConfirm", { days: settings.retention_days }))) return;
    try {
      const res = await visitorApi.post("/retention/apply", {}, { company_id: companyId });
      toast.success(t("visitors.setup.retentionApplied", { visits: res.visits, visitors: res.visitors }));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  if (!settings) return null;

  return (
    <Card>
      <CardContent className="p-6 space-y-5 max-w-xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label>{t("visitors.setup.notifyHost")}</Label>
            <p className="text-xs text-slate-500">{t("visitors.setup.notifyHostHelp")}</p>
          </div>
          <Switch
            checked={!!settings.notify_host_on_arrival}
            onCheckedChange={(c) => setSettings({ ...settings, notify_host_on_arrival: c })}
          />
        </div>
        <div>
          <Label className="mb-1 block">{t("visitors.setup.maxVisitHours")}</Label>
          <Input
            type="number"
            min={0}
            step="0.5"
            value={settings.max_visit_hours ?? ""}
            onChange={(e) => setSettings({ ...settings, max_visit_hours: e.target.value })}
          />
          <p className="text-xs text-slate-500 mt-1">{t("visitors.setup.maxVisitHoursHelp")}</p>
        </div>
        <div>
          <Label className="mb-1 block">{t("visitors.setup.retentionDays")}</Label>
          <Input
            type="number"
            min={0}
            value={settings.retention_days ?? 0}
            onChange={(e) => setSettings({ ...settings, retention_days: e.target.value })}
          />
          <p className="text-xs text-slate-500 mt-1">{t("visitors.setup.retentionHelp")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save}>{t("visitors.common.save")}</Button>
          {canManageCompanies() && Number(settings.retention_days) > 0 && (
            <Button variant="outline" className="text-red-600 border-red-200" onClick={applyRetention}>
              {t("visitors.setup.applyRetention")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
